import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { bulkUploadFootfallDataSelector } from '../../../../../modules/selectors/footfallData'
import { checkFootfallUploadStatusService } from '../../../../../modules/services/footfallData'

import { showToasts } from '../../../../../helpers/toasts'
import { TOAST_TYPE } from '../../../../../constants/other'

export function useManageFootfallDataStatus() {
  const { t } = useTranslation()
  const uploadedFiles = useSelector(bulkUploadFootfallDataSelector)

  const [socketConnection, setSocketConnection] = useState(null)
  // the hook is developed to support multiple files uploads, but for now used only for one
  const [footfallFilesProcessing, setFootfallFilesProcessing] = useState(null)

  const isFileProcessing = footfallFilesProcessing?.length > 0

  const handleFileProcessed = useCallback((taskId, socket) => {
    // remove task from processing list
    setFootfallFilesProcessing(prev => {
      const newProcessingList = prev.filter(task => task !== taskId)

      if (newProcessingList.length === 0) {
        // close socket connection if no tasks are left
        socket.close()
        setSocketConnection(null)
      }

      return newProcessingList
    })
  }, [])

  const handleStreamUpdate = useCallback(
    (response, socket) => {
      const { status, message } = response

      if (status === 'success') {
        const { task_id } = response

        showToasts({
          type: TOAST_TYPE.success,
          message: t('The CSV file has been processed successfully'),
          forceToShow: true
        })

        // remove task from processing list
        handleFileProcessed(task_id, socket)
      } else if (status === 'error') {
        const { task_id } = response

        showToasts({
          type: TOAST_TYPE.error,
          message: message,
          forceToShow: true
        })

        // remove task from processing list
        handleFileProcessed(task_id, socket)
      }
    },
    [handleFileProcessed, t]
  )

  useEffect(() => {
    const newTask = uploadedFiles?.task_id
    if (newTask) {
      setFootfallFilesProcessing(prev => {
        if (prev) {
          return [...prev, newTask]
        } else {
          return [newTask]
        }
      })
    }
  }, [uploadedFiles])

  const onSocketOpen = useCallback(socket => {
    setSocketConnection(socket)
  }, [])

  const manageSocketConnection = useCallback(() => {
    if (!socketConnection) {
      checkFootfallUploadStatusService({ onSocketOpen, handleStreamUpdate })
    }
  }, [handleStreamUpdate, onSocketOpen, socketConnection])

  return useMemo(() => ({ manageSocketConnection, isFileProcessing }), [manageSocketConnection, isFileProcessing])
}
